import { useT, itemName, resourceLabel } from '@/i18n';
import { getItem } from '@/domain/items';
import { upgradeCost, upgradedStats, maxLevel, canAfford, formatCost } from '@/domain/upgrade';
import { useGameStore } from '@/state/gameStore';
import { findInstance } from '@/domain/equipment';
import type { EquipmentInstance } from '@/domain/types';
import { ItemIcon } from './ItemIcon';
import './UpgradeModal.css';

interface UpgradeModalProps {
  instance: EquipmentInstance;
  onClose: () => void;
}

export function UpgradeModal({ instance, onClose }: UpgradeModalProps) {
  const player = useGameStore((s) => s.player);
  const upgradeInstance = useGameStore((s) => s.upgradeInstance);
  const t = useT();

  // Always look up the LIVE instance from the player store so subsequent
  // upgrades reflect the new level without closing the modal.
  const liveInstance = player ? findInstance(player, instance.id) ?? instance : instance;

  const def = getItem(liveInstance.itemId);
  if (!def || !def.stats || !player) return null;

  const curLevel = liveInstance.upgradeLevel;
  const max = maxLevel(def);
  const atMax = curLevel >= max;
  const cost = atMax ? null : upgradeCost(def, curLevel);
  const affordable = cost ? canAfford(player, cost) : false;

  const currentStats = upgradedStats(def.stats, curLevel);
  const nextStats = atMax ? currentStats : upgradedStats(def.stats, curLevel + 1);

  return (
    <div className="upgrade-modal__overlay" onClick={onClose}>
      <div className="upgrade-modal" onClick={(e) => e.stopPropagation()}>
        <div className="upgrade-modal__header">
          <ItemIcon itemId={def.id} sizeRem={3} />
          <div>
            <div className="upgrade-modal__name">
              {itemName(def.nameKey)} <span className="upgrade-modal__level">+{curLevel}</span>
            </div>
            <div className="upgrade-modal__rarity">{def.rarity}</div>
          </div>
        </div>

        {atMax ? (
          <div className="upgrade-modal__maxed">{t.character.maxLevel}</div>
        ) : (
          <>
            <div className="upgrade-modal__stats">
              <div className="upgrade-stat">
                <span className="upgrade-stat__label">{t.character.attack}</span>
                <span className="upgrade-stat__values">
                  <span>{currentStats.attack}</span>
                  <span className="upgrade-stat__arrow">→</span>
                  <span className={nextStats.attack > currentStats.attack ? 'up' : ''}>{nextStats.attack}</span>
                </span>
              </div>
              <div className="upgrade-stat">
                <span className="upgrade-stat__label">{t.character.defense}</span>
                <span className="upgrade-stat__values">
                  <span>{currentStats.defense}</span>
                  <span className="upgrade-stat__arrow">→</span>
                  <span className={nextStats.defense > currentStats.defense ? 'up' : ''}>{nextStats.defense}</span>
                </span>
              </div>
              <div className="upgrade-stat">
                <span className="upgrade-stat__label">{t.character.hp}</span>
                <span className="upgrade-stat__values">
                  <span>{currentStats.health}</span>
                  <span className="upgrade-stat__arrow">→</span>
                  <span className={nextStats.health > currentStats.health ? 'up' : ''}>{nextStats.health}</span>
                </span>
              </div>
            </div>

            <div className="upgrade-modal__cost-section">
              <div className="upgrade-modal__cost-label">{t.character.cost}:</div>
              <div className="upgrade-modal__cost-list">
                {cost && formatCost(cost).map((c) => (
                  <div key={c.key} className="upgrade-cost">
                    <span className="upgrade-cost__amount">{c.amount}</span>
                    <span className="upgrade-cost__key">{resourceLabel(c.key)}</span>
                  </div>
                ))}
              </div>
            </div>

            {!affordable && (
              <div className="upgrade-modal__warn">{t.upgrade.insufficient}</div>
            )}
          </>
        )}

        <div className="upgrade-modal__actions">
          {!atMax && (
            <button
              className={`btn ${affordable ? 'btn--primary' : 'btn--ghost'}`}
              disabled={!affordable}
              onClick={() => {
                upgradeInstance(liveInstance.id);
                // keep modal open — user can chain upgrades
              }}
            >
              {t.upgrade.confirm}
            </button>
          )}
          <button className="btn btn--ghost" onClick={onClose}>← Zurück</button>
        </div>
      </div>
    </div>
  );
}
